'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import Button from '@/ui/components/shared/Button';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import { useTranslations } from 'next-intl';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import {
  CaptchaValidationErrorCodes,
  FRIENDLY_CAPTCHA_ENDPOINT,
} from '@/integrations/captcha/constants';
import { useNukiDevices } from '@/ui/providers/nukiDevicesProvider';
import useUser from '@/ui/providers/userProvider';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { readUserMetaCurrentCookie } from '@/utils/cookie-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { ScreenToShowState } from '../../types/ScreenToShowState';
import PrivacyPolicyCheckBox from '../PrivacyPolicyCheckBox';

type FormData = {
  firstName: string;
  lastName: string;
  privacyPolicy: boolean;
};

interface NameFormProps {
  email: string;
  setScreenToShow: (screen: ScreenToShowState) => void;
  buttonText?: string;
  onCreateNewAccount?: (firstName: string, lastName: string) => void;
  // Where the signup came from; wins over the nag the visitor's tracking cookie carries.
  nag?: string;
  trackingPrefix?: string;
}

export function NameForm({
  email,
  setScreenToShow,
  buttonText,
  onCreateNewAccount,
  nag,
  trackingPrefix,
}: Readonly<NameFormProps>) {
  const t = useTranslations('ClubRegistration.NameForm');
  const captchaMessages = useTranslations('captchaMessages');
  const { nukiDevices } = useNukiDevices();
  const { refreshSessionAndCustomer } = useUser();

  const { language, country } = useLocalization();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validationSchema = yup.object().shape({
    firstName: yup.string().required(t('First name is required')),
    lastName: yup.string().required(t('Last name is required')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
  });

  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      privacyPolicy: false,
    },
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: language as any,
    showAttribution: true,
  });

  useEffect(() => {
    if (!captchaHook.captchaStatus.error && captchaHook.captchaStatus.solution) {
      setError('');
    }
  }, [captchaHook.captchaStatus]);

  const handleSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    if (captchaHook.captchaStatus.solution === null) {
      setError(captchaMessages('Please complete the CAPTCHA before submitting'));
      return;
    }

    const { firstName, lastName } = getValues();

    try {
      setLoading(true);
      const umc = readUserMetaCurrentCookie();
      const payload: Record<string, unknown> = {
        email,
        firstName,
        lastName,
        language,
        frcCaptchaSolution: captchaHook.captchaStatus.solution,
        country,
        pageUrl: globalThis.location.href,
        nukiDevices,
      };

      if (umc?.source) {
        payload.utmSource = umc.source;
      }

      if (umc?.medium) {
        payload.utmMedium = umc.medium;
      }

      if (umc?.campaignName) {
        payload.utmCampaign = umc.campaignName;
      }

      if (umc?.content) {
        payload.utmContent = umc.content;
      }

      const signupNag = nag ?? umc?.nag;
      if (signupNag) {
        payload.nag = signupNag;
      }

      const response = await axios.post(apiPaths.customers.registerViaNukiClub, payload);

      if (response.status === 200) {
        DataLayerHelper.pushSignUp('email');
        // Defensive no-op today (registration does not log the user in); kept so the
        // tile picks up a session if this endpoint ever starts issuing one.
        refreshSessionAndCustomer();
        setScreenToShow(ScreenToShowState.WELCOME);
        if (onCreateNewAccount) {
          onCreateNewAccount(firstName, lastName);
        }
      }
    } catch (requestError: any) {
      setLoading(false);

      if (requestError.response?.status === 400) {
        const serverCaptchaErrors = requestError.response.data.errors;
        if (
          serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_INVALID) ||
          serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_TIMEOUT_OR_DUPLICATE)
        ) {
          setError(captchaMessages('Captcha validation failed Please try again'));
          captchaHook.resetWidget();
          return;
        }
      }

      console.error(error);
      setError(t('An error occurred while processing your request Please try again later'));
    }
  };

  const resolvedButtonText = buttonText || t('Join the Nuki Club');
  const submitButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'name-form',
    resolvedButtonText,
  );

  return (
    <form className="mx-auto mt-6 max-w-sm space-y-4">
      <TextInput
        id="firstName"
        srLabel={t('First Name')}
        register={register}
        errors={errors.firstName}
        placeholder={t('First Name')}
        className="w-full"
        makeRounded
      />
      <TextInput
        id="lastName"
        srLabel={t('Last Name')}
        register={register}
        errors={errors.lastName}
        placeholder={t('Last Name')}
        className="w-full"
        makeRounded
      />
      <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />

      {captchaHook.CaptchaWidget({
        className: 'my-5 relative pb-4',
      })}

      <Button
        disabled={captchaHook.captchaStatus.solution === null}
        type="button"
        className="w-full"
        loading={loading}
        onClick={handleSubmit}
        data-tile-element-id={submitButtonTrackingId}
      >
        {resolvedButtonText}
      </Button>

      {error && (
        <p role="alert" className="mt-1 text-center text-sm text-red">
          {error}
        </p>
      )}
    </form>
  );
}
